/**
 * Location Wizard Store - Zustand state management for the Location creation/edit wizard
 *
 * Manages:
 * - Wizard UI state (step, open/close, create/edit mode)
 * - Step 1: Airport selection + identity fields
 * - Step 2: Airport details (auto-filled, editable)
 * - Step 3: Map pin placement + reverse-geocoded address
 * - Submission state
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { AirportAutocompleteItem } from '@/types/airports'
import type { LocationRow, LocationFormData, LocationStatus } from '@/types/locations'

// Wizard steps
export type LocationWizardStep = 0 | 1 | 2
export const LOCATION_WIZARD_STEPS = [
  'Airport & Identity',
  'Airport Details',
  'Adjust Location',
] as const

// Store state
interface LocationWizardState {
  // Wizard UI
  isOpen: boolean
  currentStep: LocationWizardStep
  highestStepVisited: LocationWizardStep
  mode: 'create' | 'edit'
  editLocationId: string | null

  // Airport reference (kept from step 1 selection)
  selectedAirport: AirportAutocompleteItem | null

  // Step 1: Airport & Identity
  airport_code: string
  name: string
  location_code: string
  status: LocationStatus

  // Step 2: Airport Details
  iata_code: string
  city: string
  state_province: string
  timezone: string
  country: string
  postal_code: string
  phone_main: string
  email_general: string

  // Step 3: Adjust Location
  latitude: number | null
  longitude: number | null
  pinLatitude: number | null
  pinLongitude: number | null
  address_line1: string
  isReverseGeocoding: boolean

  // Submission
  isSubmitting: boolean
  submissionError: string | null
}

// Store actions
interface LocationWizardActions {
  // Wizard lifecycle
  openCreate: () => void
  openEdit: (locationId: string) => void
  close: () => void
  reset: () => void

  // Navigation
  nextStep: () => void
  prevStep: () => void
  goToStep: (step: LocationWizardStep) => void

  // Step 1 actions
  setSelectedAirport: (airport: AirportAutocompleteItem | null) => void
  setName: (name: string) => void
  setLocationCode: (code: string) => void
  setAirportCode: (code: string) => void
  setStatus: (status: LocationStatus) => void

  // Step 2 actions
  setIataCode: (code: string) => void
  setCity: (city: string) => void
  setStateProvince: (state: string) => void
  setTimezone: (tz: string) => void
  setCountry: (country: string) => void
  setPostalCode: (code: string) => void
  setPhoneMain: (phone: string) => void
  setEmailGeneral: (email: string) => void

  // Step 3 actions
  setPinPosition: (lat: number, lng: number) => void
  setAddressLine1: (address: string) => void
  setReverseGeocodeResult: (result: {
    address_line1?: string
    city?: string
    state_province?: string
    postal_code?: string
    country?: string
  }) => void
  setIsReverseGeocoding: (loading: boolean) => void

  // Edit mode: bulk populate all fields
  populateFromLocation: (location: LocationRow) => void

  // Submission
  setSubmitting: (submitting: boolean) => void
  setSubmissionError: (error: string | null) => void

  // Validation helpers
  isStepComplete: (step: LocationWizardStep) => boolean
  canProceedToStep: (step: LocationWizardStep) => boolean
  getFormData: () => LocationFormData
}

// Initial state
const initialState: LocationWizardState = {
  isOpen: false,
  currentStep: 0,
  highestStepVisited: 0,
  mode: 'create',
  editLocationId: null,

  selectedAirport: null,

  // Step 1
  airport_code: '',
  name: '',
  location_code: '',
  status: 'active',

  // Step 2
  iata_code: '',
  city: '',
  state_province: '',
  timezone: '',
  country: 'US',
  postal_code: '',
  phone_main: '',
  email_general: '',

  // Step 3
  latitude: null,
  longitude: null,
  pinLatitude: null,
  pinLongitude: null,
  address_line1: '',
  isReverseGeocoding: false,

  // Submission
  isSubmitting: false,
  submissionError: null,
}

export const useLocationWizardStore = create<
  LocationWizardState & LocationWizardActions
>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Wizard lifecycle
      openCreate: () =>
        set({
          ...initialState,
          isOpen: true,
          mode: 'create',
        }),

      openEdit: (locationId) =>
        set({
          ...initialState,
          isOpen: true,
          mode: 'edit',
          editLocationId: locationId,
        }),

      close: () => set({ isOpen: false }),

      reset: () => set(initialState),

      // Navigation
      nextStep: () =>
        set((state) => {
          const next = Math.min(state.currentStep + 1, 2) as LocationWizardStep
          return {
            currentStep: next,
            highestStepVisited: Math.max(state.highestStepVisited, next) as LocationWizardStep,
          }
        }),

      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 0) as LocationWizardStep,
        })),

      goToStep: (step) =>
        set((state) => ({
          currentStep: step,
          highestStepVisited: Math.max(state.highestStepVisited, step) as LocationWizardStep,
        })),

      // Step 1 actions
      setSelectedAirport: (airport) => {
        if (airport) {
          set({
            selectedAirport: airport,
            airport_code: airport.code,
            name: airport.name,
            location_code: airport.code,
            iata_code: airport.faaCode || '',
            city: airport.city || '',
            state_province: airport.state || '',
            latitude: airport.latitude ?? null,
            longitude: airport.longitude ?? null,
            pinLatitude: airport.latitude ?? null,
            pinLongitude: airport.longitude ?? null,
          })
        } else {
          set({
            selectedAirport: null,
            airport_code: '',
            iata_code: '',
          })
        }
      },

      setName: (name) => set({ name }),
      setLocationCode: (code) => set({ location_code: code }),
      setAirportCode: (code) => set({ airport_code: code }),
      setStatus: (status) => set({ status }),

      // Step 2 actions
      setIataCode: (code) => set({ iata_code: code }),
      setCity: (city) => set({ city }),
      setStateProvince: (state_province) => set({ state_province }),
      setTimezone: (timezone) => set({ timezone }),
      setCountry: (country) => set({ country }),
      setPostalCode: (postal_code) => set({ postal_code }),
      setPhoneMain: (phone_main) => set({ phone_main }),
      setEmailGeneral: (email_general) => set({ email_general }),

      // Step 3 actions
      setPinPosition: (lat, lng) =>
        set({
          pinLatitude: lat,
          pinLongitude: lng,
          latitude: lat,
          longitude: lng,
        }),

      setAddressLine1: (address_line1) => set({ address_line1 }),

      setReverseGeocodeResult: (result) =>
        set({
          address_line1: result.address_line1 || get().address_line1,
          city: result.city || get().city,
          state_province: result.state_province || get().state_province,
          postal_code: result.postal_code || get().postal_code,
          country: result.country || get().country,
        }),

      setIsReverseGeocoding: (loading) =>
        set({ isReverseGeocoding: loading }),

      // Edit mode: bulk populate
      populateFromLocation: (location) =>
        set({
          highestStepVisited: 2,
          airport_code: location.airport_code || '',
          name: location.name || '',
          location_code: location.location_code || '',
          status: (location.status as LocationStatus) || 'active',
          iata_code: location.iata_code || '',
          city: location.city || '',
          state_province: location.state_province || '',
          timezone: location.timezone || '',
          country: location.country || 'US',
          postal_code: location.postal_code || '',
          phone_main: location.phone_main || '',
          email_general: location.email_general || '',
          latitude: location.latitude ? Number(location.latitude) : null,
          longitude: location.longitude ? Number(location.longitude) : null,
          pinLatitude: location.latitude ? Number(location.latitude) : null,
          pinLongitude: location.longitude ? Number(location.longitude) : null,
          address_line1: location.address_line1 || '',
        }),

      // Submission
      setSubmitting: (submitting) => set({ isSubmitting: submitting }),
      setSubmissionError: (error) => set({ submissionError: error }),

      // Validation helpers
      isStepComplete: (step) => {
        const state = get()
        switch (step) {
          case 0:
            return (
              state.airport_code.length >= 3 && state.name.trim().length > 0
            )
          case 1:
            // Optional fields - only mark complete once the user has navigated past
            return state.highestStepVisited > 1
          case 2:
            // Optional fields - only mark complete once user has visited
            return state.highestStepVisited >= 2
          default:
            return false
        }
      },

      canProceedToStep: (step) => {
        const state = get()
        // Step 0 (airport & identity) must have valid required fields
        if (step > 0) {
          const step0Valid =
            state.airport_code.length >= 3 && state.name.trim().length > 0
          if (!step0Valid) return false
        }
        // Steps 1 and 2 are always valid to proceed through (all optional)
        return true
      },

      getFormData: (): LocationFormData => {
        const state = get()
        return {
          name: state.name,
          airport_code: state.airport_code,
          location_code: state.location_code || undefined,
          iata_code: state.iata_code || undefined,
          latitude:
            state.pinLatitude ?? state.latitude ?? undefined,
          longitude:
            state.pinLongitude ?? state.longitude ?? undefined,
          timezone: state.timezone || undefined,
          address_line1: state.address_line1 || undefined,
          city: state.city || undefined,
          state_province: state.state_province || undefined,
          postal_code: state.postal_code || undefined,
          country: state.country || undefined,
          phone_main: state.phone_main || undefined,
          email_general: state.email_general || undefined,
          status: state.status,
        }
      },
    }),
    { name: 'location-wizard-store' }
  )
)
